// src/oura/overview/useHubActions.ts
//
// Carrying out a stage press (HubAct), once, for the two screens that show
// one: the Businesses list's row action and the business page (header pills,
// the Overview Next card, the empty states). A press that opens a page goes to
// `hubActHref` in this tab, with the screen it came from as the flow's Close
// target; a press carried out on the screen (a sheet, the signature write) is
// the caller's.
//
// Also the snackbar half of the New scan notice, which both screens show the
// same way.

import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "@hook/use-snackbar";
import { useScanDraftNotice, type ScanDraftNoticeOptions } from "../newscan/scanDraftNotice";
import { hubActHref, type HubAct } from "./hubStage";

/** The business a press is for. */
export interface HubActTarget {
    id: string;
    name: string;
    domain: string;
}

export interface HubActHandlers {
    /** Open the Client signed sheet for this business. */
    onClientSigned(target: HubActTarget): void;
    /** Open the Rescan sheet for this business. */
    onRescan(target: HubActTarget): void;
    /** Take Q{n} as the signature question. Absent: the press opens Onboarding, where it is made. */
    onSignature?(target: HubActTarget, n: number): void;
}

/** One press, carried out the same way wherever it is shown. */
export function useHubActions({ onClientSigned, onRescan, onSignature }: HubActHandlers): (target: HubActTarget, a: HubAct) => void {
    const navigate = useNavigate();
    const location = useLocation();
    const from = `${location.pathname}${location.search}`;
    return useCallback(
        (target: HubActTarget, a: HubAct) => {
            if (a.kind === "client_signed") return onClientSigned(target);
            if (a.kind === "rescan") return onRescan(target);
            if (a.kind === "signature") {
                if (onSignature) return onSignature(target, a.n);
                return navigate(hubActHref(target.id, target.domain, { kind: "onboarding", press: false }) ?? "", { state: { from } });
            }
            const to = hubActHref(target.id, target.domain, a);
            // The flow's Close comes back to the screen the press was made on.
            if (to) navigate(to, { state: { from } });
        },
        [navigate, from, onClientSigned, onRescan, onSignature],
    );
}

/** "Draft saved." or "Scan started", handed over by the New scan page as it leaves, as a snackbar with Open. */
export function useDraftNoticeSnackbar(): void {
    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const show = useCallback(
        (text: string, opts?: ScanDraftNoticeOptions) => {
            const to = opts?.to;
            snackbar.showSuccess(text, undefined, opts?.duration, to ? { actionLabel: "Open", onAction: () => navigate(to) } : undefined);
        },
        [snackbar, navigate],
    );
    useScanDraftNotice(show);
}
